import axios from "./base";
import axiosApi from "./axios";
import moment from "moment";
import { filter } from "lodash";

async function mailer(mailParams) {
  try {
    const { to, subject = "QSpace", ...params } = mailParams;

    if (!to) {
      throw Error("Should provide `to` parameter.");
    }

    const { data } = await axios.post("/mailer", {
      to,
      subject,
      ...params
    });

    return { data: data.data };
  } catch (error) {
    return { errors: [error] };
  }
}

export async function getLogo() {
  try {
    const { data } = await axios.get("/options").then(result => {
      return result.status == 200 ? result.data : [];
    });
    let defaultLogo = null;
    let logoMain = filter(data, ["key", "defaultlogo.main"]);
    if (logoMain.length && logoMain[0].value.status == false) {
      let logoSub = filter(data, ["key", "defaultlogo.sub"]);
      if (logoSub.length && logoSub[0].value.image) {
        defaultLogo = logoSub[0].value.image;
      }
    }
    return defaultLogo;
  } catch (error) {
    return { errors: [error] };
  }
}

/**
 * Build and send generic welcome email to new users.
 */
export async function signupMailer({ to, displayName }) {
  try {
    const data = await mailer({
      to,
      template: "basic",
      subject: "Thanks for Signing Up",
      locals: {
        title: "Hello There!",
        preheader: `Hi ${displayName}, Thank you for signing up. To proceed with booking a space, simply sign in using your login details and click ‘Make a Booking’ right from your dashboard.`,
        content: `
          <p>Hi ${displayName},</p>
          <p>Thank you for your signing up. To proceed with booking a space, simply <a href="${location.origin}" target="_blank">sign in</a> using your login details and click ‘Make a Booking’ right from your dashboard.</p>
          <p>We hope to see you at one of our locations soon!</p>
        `,
        logo: await getLogo()
      }
    });

    return { data };
  } catch (error) {
    return { errors: [error] };
  }
}

/**
 * Build and send generic booking confirmation email.
 */

export const LoftSpecialInstructions = dates => {
  const start = moment(dates[0].start).format("MMM D, YYYY hh:mm A");
  const end = moment(dates[dates.length - 1].end).format("MMM D, YYYY hh:mm A");

  const spcInstructions = `
  <strong>Entrance Instructions:</strong><br />
  <p>Loft, Ortigas location - Present your booking and payment confirmation upon entering the Coworking Space.</p>

  <p>Loft, BGC location - If you’ve booked a desk at our <u>BGC Location</u>, you may use the unique QR and numerical code <b>(from the next email)</b> upon entering the space. We’ve listed a few items for you to remember.</p>
  
  <p style="padding: 10px"><b>Reminders:</b></p>
  <table>
    <tr>
      <td style="padding-left: 20px; padding-right: 5px;vertical-align:top">1.</td>
      <td>
        Have your unique QR code accessible at all times (printed copy, mobile screenshot, or saved image in your mobile)
      </td>
    </tr>
    <tr>
      <td style="padding-left: 20px; padding-right: 5px;vertical-align:top">2.</td>
      <td>
      ${
        dates.length === 1
          ? `The validity of your QR code is from <b>${start} to ${end} </b>`
          : `The validity of your QR code is from start time to end time of your booking`
      }
      </td>
    </tr>
    <tr>
      <td style="padding-left: 20px; padding-right: 5px;vertical-align:top">3.</td>
      <td>
      To enter the space, <b>hover your QR code in front of the scanner camera</b> by the second door. The door shall unlock then.
      </td>
    </tr>
     <tr>
      <td style="padding-left: 20px; padding-right: 5px;vertical-align:top">4.</td>
      <td>
      For any concerns, please ring the doorbell by the second door and we’ll be more than happy to assist!
      </td>
    </tr>
    <tr>
      <td style="padding-left: 20px; padding-right: 5px;vertical-align:top">5.</td>
      <td>
      Lastly, <b>do not leave your things unattended.</b> Loft spaces is not liable for any lost/missing belongings.
      </td>
    </tr>
  </table>
  <br />
  `;
  return spcInstructions;
};

export async function bookingConfirmEmail(params) {
  const tenant = window.location.host.split(".")[0];
  const { to, fullName, details } = params;

  const loadCC = () => {
    let ccList = "";
    if (tenant === "loft") {
      ccList = "mis@loft.ph, mis@august99.com, hello@loft.ph";
    }
    return ccList;
  };

  const paymentInstruction = () => {
    if (tenant === "loft") {
      return `
      <br />
      <p>Payment Instructions:</p>
      <p>
        If you’ve already paid online using your credit card, please accept our thanks and we’ll just see you here at our location.
      </p>
      <p>If you've opted to pay via bank deposit / PayMaya, please send over the payment confirmation slip / email to hello@loft.ph for us to confirm your booking.</p>`;
    } else {
      return `
      <br />
      <p>Payment Instructions:</p>
      <p>
        If you've already paid online using your credit card, please accept our thanks and we'll just see you here at our location.
      </p>
      <p>If you've opted to pay using cash or check, please bring your payment on or before the day of your booking and our front desk staff will process it.</p>`;
    }
  };

  const footer = () => {
    if (tenant === "loft") {
      return `
      <p>Thank you and have an awesome day ahead!</p><br />
      <p>The Loft Spaces Team</p>
      `;
    } else {
      return `<p>Thank you and have an awesome day ahead!</p><br />`;
    }
  };

  // TODO: Clean quick patch and resolve to another approach.
  const specialContent = `<p><a href="https://www.loft.ph/guidelines-prevent-covid19-at-work">Click here to see Loft’s Guidelines to Prevent COVID-19 at Work</a></p>`;

  let content = `
    <p>Hi <b>${fullName}</b>,</p>
    <p>Thank you for your recent booking. We’re excited to meet you and can’t wait to show you around!</p>
    ${tenant === "loft" ? specialContent : ""}
    <p><strong>Please take note of the details of your booking below:</strong></p>
    <table style="background-color: #eee; width: 100%;">
      <tr style="padding: 10px;">
        <th style="padding: 20px 20px 0; font-size: 14px;">Product</th>
        <th>&nbsp;</th>
        <th>&nbsp;</th>
        <th style="padding: 20px 10px 0; font-size: 14px;">Term</th>
        <th>&nbsp;</th>
        <th>&nbsp;</th>
        <th style="padding: 20px 10px 0; font-size: 14px;">Qty</th>
        <th>&nbsp;</th>
        <th>&nbsp;</th>
        <th style="padding: 20px 20px 0; font-size: 14px; text-align: right;">Amount</th>
      </tr>
    `;

  details.order.map((e, i) => {
    content += `
      <tr style="padding: 10px;">
          <td style="padding: 10px 20px; font-size: 14px">${
            details.orders[i].Product.name
          }<br/>${moment(details.orders[i].OrderPackage.startAt).format(
      "MMM D, YYYY hh:mm A"
    )} to ${moment(details.orders[i].OrderPackage.endAt).format(
      "MMM D, YYYY hh:mm A"
    )}</td>
        <td>&nbsp;</td>
        <td>&nbsp;</td>
        <td style="padding: 10px; font-size: 14px; vertical-align: top;">${
          details.orders[i].Term.name
        }</td>
        <td>&nbsp;</td>
        <td>&nbsp;</td>
        <td style="padding: 10px; font-size: 14px; vertical-align: top;">
          ${details.orders[i].OrderPackage.quantity}
        </td>
        <td>&nbsp;</td>
        <td>&nbsp;</td>
        <td style="padding: 10px 20px; font-size: 14px; vertical-align: top; text-align: right;">${convertToMoney(
          details.orders[i].OrderPackage.total
        )}</td>
      </tr>
    `;
  });

  content += `
    </table>
    <br />
    <table style="background-color: #eee; width: 100%;">
  `;

  try {
    content += await getInvoiceAmount(details.invoiceId);
  } catch (error) {
    return { errors: [error] };
  }

  content += `</table>
  ${paymentInstruction()}
  ${tenant === "loft" ? LoftSpecialInstructions(details.dates) : ""}
  ${footer()}
  `;

  try {
    const data = await mailer({
      to,
      cc: loadCC(),
      subject: "Your Booking Confirmation",
      template: "basic",
      locals: {
        title: "Booking Confirmation",
        preheader: `Hi ${fullName}, Thank you for your recent booking. We’re excited to meet you and can’t wait to show you around!`,
        content: content,
        logo: await getLogo()
      }
    });

    return { data };
  } catch (error) {
    return { errors: [error] };
  }
}

/**
 * Build and send generic email for payment confirmation.
 */
// TODO: Add needed data.
export async function paymentConfirmEmail({ to, fullName, order, invoiceId }) {
  try {
    let content = `
        <p>Hi ${fullName},</p>
        <p>Thank you for your recent payment. We’re looking forward to meeting you here at our space.</p>
        <p><strong>Please take note of the details of your paid booking below:</strong></p>
        <table style="background-color: #eee; width: 100%;">
        <tr style="padding: 10px;">
          <th style="padding: 20px 20px 0; font-size: 14px;">Product</th>
          <th>&nbsp;</th>
          <th>&nbsp;</th>
          <th style="padding: 20px 10px 0; font-size: 14px;">Term</th>
          <th>&nbsp;</th>
          <th>&nbsp;</th>
          <th style="padding: 20px 10px 0; font-size: 14px;">Qty</th>
          <th>&nbsp;</th>
          <th>&nbsp;</th>
          <th style="padding: 20px 20px 0; font-size: 14px; text-align: right;">Amount</th>
        </tr>
      `;

    order.map(e => {
      content += `
          <tr style="padding: 10px;">
          <td style="padding: 10px 20px; font-size: 14px">${
            e.product
          }<br/>${moment(e.start).format("MMM D, YYYY hh:mm A")} to ${moment(
        e.end
      ).format("MMM D, YYYY hh:mm A")}</td>
            <td>&nbsp;</td>
            <td>&nbsp;</td>
            <td style="padding: 10px; font-size: 14px; vertical-align: top;">${
              e.term
            }</td>
            <td>&nbsp;</td>
            <td>&nbsp;</td>
            <td style="padding: 10px; font-size: 14px; vertical-align: top;">
              ${e.orderPackage.quantity}
            </td>
            <td>&nbsp;</td>
            <td>&nbsp;</td>
            <td style="padding: 10px 20px; font-size: 14px; vertical-align: top; text-align: right;">${convertToMoney(
              e.orderPackage.amount
            )}</td>
          </tr>
        `;
    });

    content += `
      </table>
      <br />
      <table style="background-color: #eee; width: 100%;">
    `;

    content += await getInvoiceAmount(invoiceId);

    content += `</table><p>To download a copy of your invoice, please <a href="${location.origin}" target="_blank">log in</a> to your account to view your booking history.</p>
    <p>Thanks and have an awesome day ahead!</p>`;
    const data = await mailer({
      to,
      subject: "Your Payment Confirmation",
      template: "basic",
      locals: {
        title: "Payment Confirmation",
        preheader: `Hi ${fullName}, Thank you for your recent payment. We’re looking forward to meeting you here at our space.`,
        content: content,
        logo: await getLogo()
      }
    });

    return { data };
  } catch (error) {
    return { errors: [error] };
  }
}

export async function paymentConfirmToOwner({ details }) {
  try {
    const { code, fullName, order, invoiceId } = details;
    let content = `
        <p>A new booking order has been confirmed</p>
        <table>
          <tr>
            <td style="font-size: 16px;"><b> User:</b> ${fullName}</td>
          </tr>
          <tr>
            <td style="font-size: 16px; padding-bottom: 30px;"><b>Invoice #:</b> ${code}</td>
          </tr>
        <table>
        <table style="background-color: #eee; width: 100%;">
        <tr style="padding: 10px;">
          <th style="padding: 20px 20px 0; font-size: 14px;">Product</th>
          <th>&nbsp;</th>
          <th>&nbsp;</th>
          <th style="padding: 20px 10px 0; font-size: 14px;">Term</th>
          <th>&nbsp;</th>
          <th>&nbsp;</th>
          <th style="padding: 20px 10px 0; font-size: 14px;">Qty</th>
          <th>&nbsp;</th>
          <th>&nbsp;</th>
          <th style="padding: 20px 20px 0; font-size: 14px; text-align: right;">Amount</th>
        </tr>
      `;

    order.map(e => {
      content += `
        <tr style="padding: 10px;">
          <td style="padding: 10px 20px; font-size: 14px">${
            e.product
          }<br/>${moment(e.start).format("MMM D, YYYY hh:mm A")} to ${moment(
        e.end
      ).format("MMM D, YYYY hh:mm A")}</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td style="padding: 10px; font-size: 14px; vertical-align: top;">${
            e.term
          }</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td style="padding: 10px; font-size: 14px; vertical-align: top;">
            ${e.orderPackage.quantity}
          </td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td style="padding: 10px 20px; font-size: 14px; vertical-align: top; text-align: right;">${convertToMoney(
            e.orderPackage.amount
          )}</td>
        </tr>
      `;
    });

    content += `
      </table>
      <br />
      <table style="background-color: #eee; width: 100%;">
    `;

    content += await getInvoiceAmount(invoiceId);

    content += `</table>`;

    let emailQue = await axiosApi.get(
      `/emailnotifications?notificationType="paid":true&status=1`
    );
    let emailTo = "admin@qspaceapp.com";
    if (emailQue.data.data.length) {
      let emails = emailQue.data.data;
      let arrangedEmails = [];
      emails.forEach(val => {
        arrangedEmails.push(val.email);
      });
      emailTo = arrangedEmails.join(",");
    }

    const data = await mailer({
      to: emailTo,
      subject: "New Payment Confirmation",
      template: "basic",
      locals: {
        title: "New Payment Confirmation",
        content: content,
        logo: await getLogo()
      }
    });

    return { data };
  } catch (error) {
    return { errors: [error] };
  }
}
/**
 *
 */
// TODO: Add needed data.
export async function bookingCancelEmail({ to, displayName, order }) {
  try {
    let tenant = window.location.host.split(".")[0];
    let content = `
      <p>Hi ${displayName},</p>
      <p>This is to confirm that your booking at ${tenant.toUpperCase()} has been cancelled. Here are the details:</p>
      `;
    order.OrderPackages.forEach(val => {
      content += `
        <p style="font-size: 10px;">
          <div><label style="font-weight"><b>Order:</b> ${
            val.Package.Product.name
          }</label></div>
          <div><label><b>Booking date:</b> ${moment(val.startAt).format(
            "MMM D, YYYY hh:mm A"
          )} to ${moment(val.endAt).format("MMM D, YYYY hh:mm A")}</label></div>
        </p>`;
    });
    content += `
      <p>Please contact us if you have questions about this cancellation or if you need further assistance in making a new booking.</p>
      <p>We hope to see you again at ${tenant.toUpperCase()} soon!</p>
    `;
    const data = await mailer({
      to,
      subject: "Your Booking Has Been Cancelled",
      template: "basic",
      locals: {
        title: "Booking Cancellation",
        preheader: `Hi ${displayName}, Here’s the confirmation email that your booking has been cancelled. Details are listed below.`,
        content: content,
        logo: await getLogo()
      }
    });
    return { data };
  } catch (error) {
    return { errors: [error] };
  }
}

/**
 *
 */
// TODO: Add needed data.
export async function bookingCancelEmailToOwner({ fullName, order }) {
  try {
    let content = `
      <p>Hi team,</p>
      <p>This is to confirm that a customer has cancelled their booking. Here are the details:</p>
      <p><b>Customer:</b> ${fullName}</p>
    `;
    order.OrderPackages.forEach(val => {
      content += `
        <p style="font-size: 10px;">
          <div><label style="font-weight"><b>Order:</b> ${
            val.Package.Product.name
          }</label></div>
          <div><label><b>Booking date:</b> ${moment(val.startAt).format(
            "MMM D, YYYY hh:mm A"
          )} to ${moment(val.endAt).format("MMM D, YYYY hh:mm A")}</label></div>
        </p>`;
    });

    let emailQue = await axiosApi.get(
      `/emailnotifications?notificationType="cancellation":true&status=1`
    );
    let emailTo = "admin@qspaceapp.com";
    if (emailQue.data.data.length) {
      let emails = emailQue.data.data;
      let arrangedEmails = [];
      emails.forEach(val => {
        arrangedEmails.push(val.email);
      });
      emailTo = arrangedEmails.join(",");
    }

    const data = await mailer({
      to: emailTo,
      subject: "Your Booking Has Been Cancelled",
      template: "basic",
      locals: {
        title: "Booking Cancellation",
        content: content,
        logo: await getLogo()
      }
    });
    return { data };
  } catch (error) {
    return { errors: [error] };
  }
}

export async function reportBugEmailer(params) {
  const { subject, message, user } = params;
  let emailQue = await axiosApi.get(
    `/emailnotifications?notificationType="bug":true&status=1`
  );
  // TODO Enable dynamic input for receiver on admin settings.
  let emailTo = "support@qspaceapp.com";
  if (emailQue.data.data.length) {
    let emails = emailQue.data.data;
    let arrangedEmails = [];
    emails.forEach(val => {
      arrangedEmails.push(val.email);
    });
    emailTo = arrangedEmails.join(",");
  }
  try {
    const data = await mailer({
      to: emailTo,
      subject: "Heads Up! New Bug Reported on QSpace",
      template: "basic",
      locals: {
        title: subject,
        preheader: `Bug Report from QSpace - `,
        content: `
        <p>Name: ${user.firstName} ${user.lastName}</p>
        <p>Email: ${user.email}</p>
        <p>Message: ${message}</p>
        `,
        logo: await getLogo()
      }
    });
    return { data };
  } catch (error) {
    return { errors: [error] };
  }
}

export async function reportAnIssueMailer(params) {
  const { subject, message, user } = params;
  let emailQue = await axiosApi.get(
    `/emailnotifications?notificationType="issue":true&status=1`
  );

  // TODO Enable dynamic input for receiver on admin settings.
  let emailTo = "support@qspaceapp.com";
  if (emailQue.data.data.length) {
    let emails = emailQue.data.data;
    let arrangedEmails = [];
    emails.forEach(val => {
      arrangedEmails.push(val.email);
    });
    emailTo = arrangedEmails.join(",");
  }
  try {
    const data = await mailer({
      to: emailTo,
      subject: "Take Action: New Issue Reported",
      template: "basic",
      locals: {
        title: subject,
        preheader: `Issue Report from QSpace - `,
        content: `
          <p>User: ${user.firstName} ${user.lastName}</p>
          <p>Issue: ${message.issue}</p>
          <p>Location: ${message.location}</p>
          <p>Space: ${message.product}</p>
          <p>Description: </br> ${message.description}</p>
        `,
        logo: await getLogo()
      }
    });
    return { data };
  } catch (error) {
    return { errors: [error] };
  }
}

export async function feedBackMailer({ subject, message }) {
  let emailQue = await axiosApi.get(
    `/emailnotifications?notificationType="feedback":true&status=1`
  );

  let emailTo = "admin@qspaceapp.com";
  if (emailQue.data.data.length) {
    let emails = emailQue.data.data;
    let arrangedEmails = [];
    emails.forEach(val => {
      arrangedEmails.push(val.email);
    });
    emailTo = arrangedEmails.join(",");
  }

  try {
    const data = await mailer({
      to: emailTo,
      subject: "Heads up: New Feedback from Member",
      template: "basic",
      locals: {
        title: subject,
        preheader: `Feedback from QSpace - `,
        content: `
        <p>Experience Rating: ${message.rate}</p>
        <p>Feedback: ${message.feedback}</p>
        <p>User: ${message.user}</p>
        <p>Email: ${message.email}</p>
        `,
        logo: await getLogo()
      }
    });
    return { data };
  } catch (error) {
    return { errors: [error] };
  }
}

export async function bookingConfirmNotifyOwner({ details }) {
  const { invoiceId, code, fullName, email, orders } = details;
  let content = `
    <p>A new booking order has been added</p>
    <table>
      <tr>
        <td style="font-size: 16px;"><b> User:</b> ${fullName}</td>
      </tr>
      <tr>
        <td style="font-size: 16px;"> 
          <b>Email Address:</b> <a href="mailto:${email}" style="color:#333;text-decoration:none">${email}</a>
        </td>
      </tr>
      <tr>
        <td style="font-size: 16px; padding-bottom: 30px;"><b>Invoice #:</b> ${code}</td>
      </tr>
    <table>
    <table style="background-color: #eee; width: 100%;">
      <tr style="padding: 10px;">
        <th style="padding: 20px 20px 0; font-size: 14px;">Product</th>
        <th>&nbsp;</th>
        <th>&nbsp;</th>
        <th style="padding: 20px 10px 0; font-size: 14px;">Term</th>
        <th>&nbsp;</th>
        <th>&nbsp;</th>
        <th style="padding: 20px 10px 0; font-size: 14px;">Qty</th>
        <th>&nbsp;</th>
        <th>&nbsp;</th>
        <th style="padding: 20px 20px 0; font-size: 14px; text-align: right;">Amount</th>
      </tr>
    `;

  orders.map(e => {
    content += `
        <tr style="padding: 10px;">
          <td style="padding: 10px 20px; font-size: 14px">${
            e.Product.name
          }<br/>${moment(e.OrderPackage.startAt).format(
      "MMM D, YYYY hh:mm A"
    )} to ${moment(e.OrderPackage.endAt).format("MMM D, YYYY hh:mm A")}</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td style="padding: 10px; font-size: 14px; vertical-align: top;">${
            e.Term.name
          }</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td style="padding: 10px; font-size: 14px; vertical-align: top;">
            ${e.OrderPackage.quantity}
          </td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td style="padding: 10px 20px; font-size: 14px; vertical-align: top; text-align: right;">${convertToMoney(
            e.OrderPackage.total
          )}</td>
        </tr>
      `;
  });
  content += `
    </table>
    <br />
    <table style="background-color: #eee; width: 100%;">
  `;

  try {
    let emailQue = await axiosApi.get(
      `/emailnotifications?notificationType="makeBooking":true&status=1`
    );
    let emailTo = "admin@qspaceapp.com";
    if (emailQue.data.data.length) {
      let emails = emailQue.data.data;
      let arrangedEmails = [];
      emails.forEach(val => {
        arrangedEmails.push(val.email);
      });
      emailTo = arrangedEmails.join(",");
    }
    content += await getInvoiceAmount(invoiceId);
    content += `</table>`;

    const data = await mailer({
      to: emailTo,
      subject: "New Booking Confirmation",
      template: "basic",
      locals: {
        title: "New User Booking Confirmation",
        content: content,
        logo: await getLogo()
      }
    });

    return { data };
  } catch (error) {
    return { errors: [error] };
  }
}

function convertToMoney(amount) {
  return (
    "Php " +
    amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  );
}

async function getInvoiceAmount(invoiceId) {
  let content = "";
  await axiosApi.get(`/orders/${invoiceId}`).then(res => {
    content = `
      <tr>
        <td style="padding: 20px 10px 10px 20px; font-size: 14px; font-weight: bold;">Total Due</td>
      </tr>
      <tr>
        <td style="padding: 5px 10px 0 20px; font-size: 14px;">Amount</td>
        <td style="padding: 5px 20px; font-size: 14px; text-align: right;">${convertToMoney(
          res.data.data.totalAmount / 1.12
        )}</td>
      <tr/>
      <tr>
        <td style="padding: 5px 10px 0 20px; font-size: 14px;">Input Vat</td>
        <td style="padding: 5px 20px; font-size: 14px; text-align: right;">${convertToMoney(
          res.data.data.totalAmount - res.data.data.totalAmount / 1.12
        )}</td>
      <tr/>
      <tr>
      <td style="padding: 5px 10px 20px 20px; font-size: 16px; font-weight: bold;">Total</td>
      <td style="padding: 5px 20px 20px; font-size: 16px; text-align: right; font-weight: bold;">${convertToMoney(
        res.data.data.totalAmount
      )}</td>
      <tr/>
      `;
  });
  return content;
}

export async function forgotPasswordMailer({ name, to, resetTokenUrl }) {
  try {
    const data = await mailer({
      to,
      template: "basic",
      subject: "Reset your Password",
      locals: {
        preheader: "",
        title: "",
        content: `
          <p>Hi ${name},</p>
          <p>We've received a request to reset your password. If you didn't make this request, please ignore this email.</p><br />
          <p style="text-align: center;">
            <a href="${resetTokenUrl}" target="_blank" style="background-color:#51b67e;border:0;color:#fff;cursor:pointer;display:inline-block;font-size:1rem;font-weight:bolder;padding:0.75em 1.25em;text-decoration:none;">
              Click here to reset your password
            </a>
          </p><br />
          <p>Otherwise you can reset your password using this link:<br /> ${resetTokenUrl}</p>
        `,
        logo: await getLogo()
      }
    });

    return { data };
  } catch (error) {
    return { errors: [error] };
  }
}

export async function confirmPasswordResetMailer({
  to,
  displayName,
  loginUrl = location.origin
}) {
  try {
    let tenantId = window.location.host.split(".")[0];
    const {
      data: { name: tenantName = "QSpace" }
    } = (await axios.get(`/tenants/${tenantId}`)).data;
    const data = await mailer({
      to,
      template: "basic",
      subject: "Password Reset Successful!",
      locals: {
        preheader: "",
        title: "",
        content: `
          <p>Hi ${displayName},</p>
          <p>This is to confirm that you've successfully reset your password for ${tenantName}.</p>
          <p>Click <a href="${loginUrl}" target="_blank">here</a> to log in to your dashboard.</p>
          <p>
            ${tenantName} Team
          </p>
        `,
        logo: await getLogo()
      }
    });

    return { data };
  } catch (error) {
    return { errors: [error] };
  }
}

/**
 * Build and send generic welcome email to new users.
 */
export async function signupViaBulkUpload({ to, displayName, resetTokenUrl }) {
  try {
    const data = await mailer({
      to,
      template: "basic",
      subject: "Thanks for Signing Up",
      locals: {
        title: "Hello There!",
        preheader: `Hi ${displayName}, Thank you for signing up. To proceed with booking a space, simply sign in using your login details and click ‘Make a Booking’ right from your dashboard.`,
        content: `
          <p>Hi ${displayName},</p>
          <p>Thank you for your signing up. To proceed with booking a space, simply sign in please set your password by pressing the button.</p>
          <p style="text-align: center;">
            <a href="${resetTokenUrl}" target="_blank" style="background-color:#51b67e;border:0;color:#fff;cursor:pointer;display:inline-block;font-size:1rem;font-weight:bolder;padding:0.75em 1.25em;text-decoration:none;">
              Click here to set your password
            </a>
          </p><br />
          <p>Otherwise you can set your password using this link:<br /> ${resetTokenUrl}</p>
          <p>We hope to see you at one of our locations soon!</p>
        `,
        logo: await getLogo()
      }
    });

    return { data };
  } catch (error) {
    return { errors: [error] };
  }
}

export default mailer;
